/**
 * Les sujets entiers d'annales : le catalogue, ce qui a été donné, ce qui est rendu.
 *
 * Hedi, le 26 septembre 2026 : *« Pour Prépabac crée une nouvelle catégorie :
 * Sujet entier à côté de entraînement. »* Un sujet n'est pas une série
 * d'exercices : il se fait en entier, sur papier, en temps limité, et se juge
 * en entier avec le corrigé de l'éditeur. D'où un sous-onglet à part des
 * entraînements, et non une ligne de plus dans une feuille.
 *
 * **Donner envoie tout de suite** : il n'y a rien à assembler, donc pas d'état
 * « ouvert » comme pour une feuille. Tant que l'enfant n'a pas rendu sa copie,
 * le sujet peut être retiré ; une fois rendu, il ne se retire plus.
 *
 * Les sujets de l'**ancien programme** (chapitres 7, 9, 15 et 18 de l'édition
 * 2014) restent au catalogue et se donnent : l'écran le dit, le parent décide.
 * Une durée absente est écrite « durée non indiquée » — l'éditeur n'en donne pas
 * pour ces sujets, et en inventer une ferait chronométrer un travail sur un
 * chiffre qui ne vient de personne.
 *
 * Le jugement ne se fait pas ici mais dans « À valider », avec le reste de ce
 * qui attend un parent. Le verdict n'écrit rien dans `mastery` (règle 3 : un
 * sujet n'est pas vérifié).
 *
 * Au lycée, une carte en tête porte **le sujet de bac du dimanche**
 * (`SujetDuDimanche`, §17.12) : proposé par le rituel, changeable, chronométré
 * par le parent, noté au barème dans « À valider ».
 *
 * La confirmation du retrait est rendue dans la page, jamais par
 * `window.confirm` (refus silencieux dans la coquille Android).
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type {
  CatalogueDeSujetsOut,
  SujetDonneOut,
  SujetDuCatalogueOut,
} from "@/api/schema";
import { enClair } from "@/ui/dates";
import { Decoupe } from "@/ui/Decoupe";
import { useEnfants } from "@/ui/enfants";
import { LiensDImpression } from "@/ui/impression";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { entrainementsDe } from "@/v2/chemins";
import { surVingt } from "@/v2/noteAuBareme";
import { SujetDuDimanche } from "@/v2/SujetDuDimanche";

/** Le verdict d'un parent, dans ses mots et non dans ceux de la base. */
const VERDICTS: Record<string, string> = {
  juste: "juste",
  moitie: "à moitié",
  faux: "faux",
};

const GENRES: Record<string, string> = {
  bac: "Sujet type bac",
  approfondissement: "Approfondissement",
};

export function duree(minutes: number | null | undefined): string {
  if (minutes == null) return "durée non indiquée";
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return reste ? `${heures} h ${String(reste).padStart(2, "0")}` : `${heures} h`;
}

function Mentions({
  genre,
  dureeMin,
  page,
  ancien,
}: {
  genre?: string | null;
  dureeMin?: number | null;
  page?: number | null;
  ancien?: boolean;
}) {
  return (
    <p className="muted small">
      {genre ? `${GENRES[genre] ?? genre} — ` : null}
      {duree(dureeMin)}
      {page != null ? ` — page ${page}` : null}
      {ancien ? " — ancien programme" : null}
    </p>
  );
}

function Retirer({ retirer }: { retirer: () => Promise<void> }) {
  const [etat, setEtat] = useState<"ferme" | "confirme" | "envoi">("ferme");

  if (etat === "ferme") {
    return (
      <Button type="button" variant="quiet" onClick={() => setEtat("confirme")}>
        Retirer
      </Button>
    );
  }
  return (
    <div className="notice">
      <p>Retirer ce sujet ? L'enfant ne le verra plus.</p>
      <div className="screen__actions">
        <Button
          type="button"
          disabled={etat === "envoi"}
          onClick={() => {
            setEtat("envoi");
            void retirer().catch(() => setEtat("confirme"));
          }}
        >
          {etat === "envoi" ? "Retrait…" : "Oui, retirer"}
        </Button>
        <Button type="button" variant="quiet" onClick={() => setEtat("ferme")}>
          Annuler
        </Button>
      </div>
    </div>
  );
}

function Pages({ urls, quoi }: { urls: string[]; quoi: string }) {
  if (urls.length === 0) return null;
  return (
    <>
      {urls.map((url, rang) => (
        <Decoupe
          key={url}
          urls={[url]}
          intitule={urls.length > 1 ? `${quoi}, page ${rang + 1}` : quoi}
        />
      ))}
    </>
  );
}

function SujetDuCatalogue({
  sujet,
  donner,
}: {
  sujet: SujetDuCatalogueOut;
  donner: () => Promise<void>;
}) {
  const [envoi, setEnvoi] = useState(false);
  const pages = sujet.pages ?? 0;
  return (
    <li className="list__item">
      <p>
        {sujet.numero}. {sujet.titre}
      </p>
      <Mentions
        genre={sujet.genre}
        dureeMin={sujet.duree_min}
        page={sujet.page}
        ancien={sujet.ancien_programme}
      />
      {pages > 1 ? <p className="muted small">{pages} pages d'énoncé</p> : null}
      {sujet.en_attente ? (
        <p className="muted small">Déjà donné, pas encore jugé.</p>
      ) : (
        <Button
          type="button"
          disabled={envoi}
          onClick={() => {
            setEnvoi(true);
            void donner().finally(() => setEnvoi(false));
          }}
        >
          {envoi ? "Envoi…" : "Donner"}
        </Button>
      )}
    </li>
  );
}

function Catalogue({
  catalogue,
  donner,
}: {
  catalogue: CatalogueDeSujetsOut;
  donner: (identifiant: string) => Promise<void>;
}) {
  return (
    <Card>
      <p className="card__title">{catalogue.titre}</p>
      {catalogue.editeur ? <p className="muted small">{catalogue.editeur}</p> : null}
      {(catalogue.chapitres ?? []).map((chapitre) => (
        <details key={chapitre.cle}>
          <summary>
            {chapitre.numero}. {chapitre.titre}
            {chapitre.ancien_programme ? " — ancien programme" : ""} (
            {(chapitre.sujets ?? []).length})
          </summary>
          <ul className="list">
            {(chapitre.sujets ?? []).map((sujet) => (
              <SujetDuCatalogue
                key={sujet.identifiant}
                sujet={sujet}
                donner={() => donner(sujet.identifiant)}
              />
            ))}
          </ul>
        </details>
      ))}
    </Card>
  );
}

function SujetDonne({
  childId,
  sujet,
  retirer,
}: {
  childId: string;
  sujet: SujetDonneOut;
  retirer?: () => Promise<void>;
}) {
  const photos = sujet.photos ?? [];
  return (
    <li className="list__item">
      <p>
        {sujet.titre} <span className="muted small">— {sujet.chapitre_titre}</span>
      </p>
      <Mentions
        genre={sujet.genre}
        dureeMin={sujet.duree_min}
        page={sujet.page}
        ancien={sujet.ancien_programme}
      />
      <p className="muted small">
        Donné le {enClair(sujet.given_at)}
        {sujet.rendu_at ? ` — rendu le ${enClair(sujet.rendu_at)}` : null}
        {sujet.rendu_at ? ` — ${photos.length} page${photos.length > 1 ? "s" : ""} de copie` : null}
      </p>
      {sujet.verdict ? (
        <p>
          {sujet.note_sur_20 != null
            ? `Noté ${surVingt(sujet.note_sur_20)} au barème`
            : `Jugé ${VERDICTS[sujet.verdict] ?? sujet.verdict}`}
          {sujet.verdict_at ? ` le ${enClair(sujet.verdict_at)}` : null}
          {sujet.commentaire ? ` — « ${sujet.commentaire} »` : null}
        </p>
      ) : null}
      {/* Pour imprimer : le sujet se pose devant l'enfant, le corrigé reste
          chez l'adulte — deux documents, jamais un seul. */}
      <LiensDImpression
        cible={{ quoi: "sujet", childId, sujetDonneId: sujet.id }}
        corrige={(sujet.corrige_urls ?? []).length > 0}
      />
      <details>
        <summary>L'énoncé et le corrigé</summary>
        <Pages urls={sujet.enonce_urls ?? []} quoi="Énoncé" />
        <Pages urls={sujet.corrige_urls ?? []} quoi="Corrigé" />
      </details>
      {retirer ? <Retirer retirer={retirer} /> : null}
    </li>
  );
}

export function SujetsScreen() {
  const { childId = "" } = useParams();
  const enfants = useEnfants();
  const prenom = enfants.find((enfant) => enfant.child_id === childId)?.display_name;
  const [catalogues, setCatalogues] = useState<CatalogueDeSujetsOut[] | null>(null);
  const [donnes, setDonnes] = useState<SujetDonneOut[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tour, setTour] = useState(0);
  const recharger = () => setTour((avant) => avant + 1);

  const charger = useCallback(async () => {
    const params = { child_id: childId };
    const [catalogue, liste] = await Promise.all([
      call("catalogueDeSujets", { params }),
      call("sujetsDeLEnfant", { params }),
    ]);
    return { catalogues: catalogue.catalogues ?? [], donnes: liste.sujets ?? [] };
  }, [childId]);

  useEffect(() => {
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const lus = await charger();
        if (!annule) {
          setCatalogues(lus.catalogues);
          setDonnes(lus.donnes);
        }
      } catch (echec) {
        if (!annule) {
          setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [charger, tour]);

  const agir = async (action: () => Promise<unknown>) => {
    setErreur(null);
    try {
      await action();
      recharger();
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.message : "L'action a échoué.");
      throw echec;
    }
  };

  const donner = (sujet: string) =>
    agir(() => call("donnerUnSujet", { params: { child_id: childId }, body: { sujet } })).catch(
      () => undefined,
    );

  const aFaire = (donnes ?? []).filter((sujet) => !sujet.rendu_at);
  const rendus = (donnes ?? []).filter((sujet) => sujet.rendu_at && !sujet.verdict);
  const juges = (donnes ?? []).filter((sujet) => sujet.verdict);

  return (
    <Screen
      title="Les sujets entiers"
      lead={
        prenom
          ? `Un sujet d'annales à faire en entier par ${prenom}, sur papier et en temps limité`
          : undefined
      }
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      <SujetDuDimanche childId={childId} prenom={prenom} apresChangement={recharger} />
      {!donnes && !erreur ? <Loading /> : null}

      {donnes ? (
        <Card>
          <p className="card__title">À faire</p>
          {aFaire.length === 0 ? (
            <Empty>Aucun sujet en cours.</Empty>
          ) : (
            <ul className="list">
              {aFaire.map((sujet) => (
                <SujetDonne
                  key={sujet.id}
                  childId={childId}
                  sujet={sujet}
                  retirer={
                    // Le sujet du dimanche ne se retire pas : il se change, dans
                    // la carte du dessus.
                    sujet.dimanche
                      ? undefined
                      : () =>
                          agir(() =>
                            call("retirerUnSujet", {
                              params: { child_id: childId, sujet_donne_id: sujet.id },
                            }),
                          ).then(() => undefined)
                  }
                />
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {rendus.length > 0 ? (
        <Card>
          <p className="card__title">Rendus, à juger</p>
          <ul className="list">
            {rendus.map((sujet) => (
              <SujetDonne key={sujet.id} childId={childId} sujet={sujet} />
            ))}
          </ul>
          <p className="muted small">
            <Link to={`${entrainementsDe(childId)}/a-valider`}>Juger les copies rendues</Link>
          </p>
        </Card>
      ) : null}

      {juges.length > 0 ? (
        <Card>
          <p className="card__title">Jugés</p>
          <ul className="list">
            {juges.map((sujet) => (
              <SujetDonne key={sujet.id} childId={childId} sujet={sujet} />
            ))}
          </ul>
        </Card>
      ) : null}

      {catalogues && catalogues.length === 0 ? (
        <Card>
          <Empty>Aucun recueil de sujets ne correspond à la classe et aux matières de l'enfant.</Empty>
        </Card>
      ) : null}
      {(catalogues ?? []).map((catalogue) => (
        <Catalogue key={catalogue.ouvrage} catalogue={catalogue} donner={donner} />
      ))}
    </Screen>
  );
}
